import { z } from "zod";
import { patientGroupSchema, type PatientGroup } from "./patient-group";
import {
  treatmentStartState,
  CONFIRM_PRESCRIPTION_CTA,
  PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION_LABEL,
} from "./treatment-start";

// TES-1117 (Emelie 2026-10-05) — the precise onboarding status per patient
// journey. Before this, admin showed "Receptövertag" + "Onboarding" for every
// patient between F-01 and her first dose, so nobody could tell whether she
// had opened the app, saved a draft or was waiting on the doctor. Emelie:
//
//   "Journey and current status are separate dimensions, e.g. Resa:"
//   "Receptövertag / Aktuell status: F-01 klart – inväntar F-02B."
//
// One derivation, run on the server (the API puts its result on the patient
// list row and on the patient detail), so the list and the overview card can
// never disagree. It reads only stored facts; nothing here is reported by a
// client.
//
// LABELS. Where Emelie gave a string it is used verbatim (her hyphen in
// "F-01"/"F-02B" is written as the ASCII hyphen the rest of admin uses). The
// rest are existing admin labels (clinical stage, transfer review status,
// journey state, the work-queue title) or, where nothing existed, marked
// "TES-1117: proposed" below and listed in the PR for her to confirm.

export const ONBOARDING_STATUSES = [
  // Both journeys, before F-01.
  "f01_not_started",
  // "Receptövertag" (transfer), in Emelie's order (decision 3).
  "transfer_f01_done",
  "transfer_declaration_not_started",
  "transfer_declaration_in_progress",
  "transfer_f02b_done",
  "transfer_f07_in_progress",
  "transfer_submitted",
  // The review's own need-driven outcomes (TES-762) before a decision.
  "transfer_meeting_required",
  "transfer_meeting_booked",
  // Ny behandling.
  "new_f01_done",
  "new_meeting_booked_declaration_remaining",
  "new_meeting_booked_declaration_done",
  "new_meeting_held",
  // TES-1602 (TES-1600 S2) — the optional care meeting track
  // (features.careMeetingOptional): the declaration is in with no meeting
  // booked, or the doctor has required one.
  "new_declaration_submitted",
  "new_meeting_required",
  // TES-1646 (Ö-1015) — the declaration is in and the assessment is hers to
  // choose: not chosen yet, or a meeting chosen and not booked yet.
  "new_assessment_choice_pending",
  "new_meeting_chosen",
  // TES-1650 (Ö-1006) — started, submitted and complete are separate
  // statuses on the optional track: the declaration started and not sent,
  // and the written assessment chosen with her material not yet complete
  // and handed over (or waiting for her to re-confirm F-01). Complete is
  // new_declaration_submitted.
  "new_declaration_in_progress",
  "new_written_material_remaining",
  // Shared, after the doctor has seen her.
  "awaiting_completion",
  "completion_ready",
  "awaiting_blood_test",
  "blood_test_results_in",
  "approved",
  "awaiting_subscription",
  "subscription_active_awaiting_prescription",
  // TES-1150 — the doctor's session is complete, the subscription is not.
  "prescription_issued_awaiting_subscription",
  "prescription_issued",
  "treatment_started",
  "transfer_declined",
  "new_declined",
  "paused",
  "ended",
] as const;
export const onboardingStatusKeySchema = z.enum(ONBOARDING_STATUSES);
export type OnboardingStatusKey = z.infer<typeof onboardingStatusKeySchema>;

// The decision's own outcome, as the timeline's decision step names it. Not a
// status: it stays true after the subscription and the prescription.
export const DECISION_APPROVED_LABEL = "Godkänd för behandling";
// TES-1150 — a published positive decision whose prescription step the
// doctor has not finished. Proposed wording.
export const PRESCRIPTION_NOT_REGISTERED_LABEL =
  "Godkänd – receptstatus ej bekräftad";

export const ONBOARDING_STATUS_LABEL: Record<OnboardingStatusKey, string> = {
  // TES-1117: proposed.
  f01_not_started: "F-01 inte påbörjad",
  // Emelie, decision 3, verbatim.
  transfer_f01_done: "F-01 klart – fortsätt i appen",
  transfer_declaration_not_started: "Medicinsk hälsodeklaration inte påbörjad",
  transfer_declaration_in_progress: "Medicinsk hälsodeklaration pågår",
  transfer_f02b_done: "F-02B klar – uppgifter om pågående behandling återstår",
  transfer_f07_in_progress: "Uppgifter om pågående behandling pågår",
  transfer_submitted: "Underlag inskickat – inväntar medicinsk bedömning",
  // Existing transfer review / journey labels (state-labels.ts).
  transfer_meeting_required: "Vårdmöte krävs",
  transfer_meeting_booked: "Vårdmöte bokat",
  // TES-1117: proposed.
  new_f01_done: "F-01 klart – vårdmöte ej bokat",
  // Emelie, decision 2: "Vårdmöte bokat – hälsodeklaration klar/återstår".
  new_meeting_booked_declaration_remaining:
    "Vårdmöte bokat – hälsodeklaration återstår",
  new_meeting_booked_declaration_done: "Vårdmöte bokat – hälsodeklaration klar",
  // Emelie, Ö-344 and TES-1127, verbatim: the detail line after the meeting
  // until the final decision is published.
  new_meeting_held: "Vårdmöte genomfört – läkaren slutför bedömningen",
  // TES-1602 — the transfer's label. Ö-957 ✅ (Simon, 2026-10-30, TES-1640).
  new_declaration_submitted:
    "Underlag inskickat – inväntar medicinsk bedömning",
  // The transfer review's own label for the same doctor decision.
  new_meeting_required: "Vårdmöte krävs",
  // TES-1646: proposed.
  new_assessment_choice_pending:
    "Hälsodeklaration inskickad – bedömningssätt ej valt",
  new_meeting_chosen: "Vårdmöte valt – ej bokat",
  // TES-1650: proposed.
  new_declaration_in_progress: "Hälsodeklaration pågår",
  new_written_material_remaining:
    "Hälsodeklaration inskickad – underlag återstår",
  // Existing clinical-stage / queue / journey labels.
  awaiting_completion: "Inväntar komplettering",
  completion_ready: "Kompletteringar klara",
  awaiting_blood_test: "Inväntar blodprov",
  blood_test_results_in: "Provsvar inkomna",
  // TES-1150 (Emelie 2026-10-07): the doctor registers the prescription in
  // the same session as the decision, before the subscription. Until he has,
  // the status says his step is open, with or without a link sent and with
  // or without a payment (TES-1141's "Inväntar aktiverat abonnemang" said
  // the opposite: that the prescription must wait).
  approved: PRESCRIPTION_NOT_REGISTERED_LABEL,
  awaiting_subscription: PRESCRIPTION_NOT_REGISTERED_LABEL,
  subscription_active_awaiting_prescription:
    "Abonnemang aktiverat – receptstatus ej bekräftad",
  // Emelie's words, verbatim.
  prescription_issued_awaiting_subscription:
    PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION_LABEL,
  // TES-1117: proposed (her funnel step "recept utfärdat/behandling startad").
  prescription_issued: "Recept utfärdat",
  treatment_started: "Behandling startad",
  transfer_declined: "Kan inte tas över",
  // TES-1117: proposed.
  new_declined: "Ej godkänd för behandling",
  paused: "Pausad",
  ended: "Avslutad",
};

// Emelie, decision 2: the shared top-level status once the journeys are
// equivalent — a transfer patient with BOTH F-02B and F-07 in, a new-treatment
// patient with a booked meeting — each with its own detail line.
export const AWAITING_MEDICAL_ASSESSMENT_LABEL = "Inväntar medicinsk bedömning";
export const TRANSFER_ASSESSMENT_DETAIL =
  "Medicinskt underlag inskickat – väntar på läkargranskning";

const AWAITING_MEDICAL_ASSESSMENT = new Set<OnboardingStatusKey>([
  "transfer_submitted",
  "new_declaration_submitted",
  "new_meeting_booked_declaration_remaining",
  "new_meeting_booked_declaration_done",
  "new_meeting_held",
]);

export const ONBOARDING_WAITING_ON = ["patient", "doctor", "system"] as const;
export const onboardingWaitingOnSchema = z.enum(ONBOARDING_WAITING_ON);
export type OnboardingWaitingOn = z.infer<typeof onboardingWaitingOnSchema>;

// TES-1117: proposed.
export const ONBOARDING_WAITING_ON_LABEL: Record<OnboardingWaitingOn, string> =
  {
    patient: "Patienten",
    doctor: "Läkaren",
    system: "Systemet",
  };

// Who the next move belongs to, and what it is. null = nothing is owed: the
// onboarding is over (started, declined, ended). Next-step strings are
// TES-1117 proposals except the confirm CTA, which is TES-1069's.
const NEXT: Record<
  OnboardingStatusKey,
  { waitingOn: OnboardingWaitingOn; nextStep: string } | null
> = {
  f01_not_started: {
    waitingOn: "patient",
    nextStep: "Patienten besvarar F-01",
  },
  transfer_f01_done: {
    waitingOn: "patient",
    nextStep: "Patienten loggar in i appen och påbörjar F-02B",
  },
  transfer_declaration_not_started: {
    waitingOn: "patient",
    nextStep: "Patienten påbörjar F-02B",
  },
  transfer_declaration_in_progress: {
    waitingOn: "patient",
    nextStep: "Patienten skickar in F-02B",
  },
  transfer_f02b_done: {
    waitingOn: "patient",
    nextStep: "Patienten fyller i F-07",
  },
  transfer_f07_in_progress: {
    waitingOn: "patient",
    nextStep: "Patienten skickar in F-07",
  },
  transfer_submitted: {
    waitingOn: "doctor",
    nextStep: "Läkaren granskar underlaget",
  },
  transfer_meeting_required: {
    waitingOn: "patient",
    nextStep: "Patienten bokar vårdmöte",
  },
  transfer_meeting_booked: {
    waitingOn: "doctor",
    nextStep: "Vårdmötet genomförs",
  },
  new_f01_done: { waitingOn: "patient", nextStep: "Patienten bokar vårdmöte" },
  new_meeting_booked_declaration_remaining: {
    waitingOn: "patient",
    nextStep: "Patienten skickar in hälsodeklarationen",
  },
  new_meeting_booked_declaration_done: {
    waitingOn: "doctor",
    nextStep: "Vårdmötet genomförs",
  },
  new_meeting_held: {
    waitingOn: "doctor",
    nextStep: "Läkaren fattar behandlingsbeslut",
  },
  new_declaration_submitted: {
    waitingOn: "doctor",
    nextStep: "Läkaren granskar underlaget",
  },
  new_meeting_required: {
    waitingOn: "patient",
    nextStep: "Patienten bokar vårdmöte",
  },
  // TES-1646: proposed.
  new_assessment_choice_pending: {
    waitingOn: "patient",
    nextStep: "Patienten väljer skriftlig bedömning eller vårdmöte",
  },
  new_meeting_chosen: {
    waitingOn: "patient",
    nextStep: "Patienten bokar vårdmöte",
  },
  // TES-1650: proposed.
  new_declaration_in_progress: {
    waitingOn: "patient",
    nextStep: "Patienten skickar in hälsodeklarationen",
  },
  new_written_material_remaining: {
    waitingOn: "patient",
    nextStep: "Patienten slutför underlaget för skriftlig bedömning",
  },
  awaiting_completion: {
    waitingOn: "patient",
    nextStep: "Patienten kompletterar",
  },
  completion_ready: {
    waitingOn: "doctor",
    nextStep: "Läkaren granskar kompletteringen",
  },
  awaiting_blood_test: {
    waitingOn: "patient",
    nextStep: "Patienten lämnar blodprov",
  },
  blood_test_results_in: {
    waitingOn: "doctor",
    nextStep: "Läkaren granskar provsvaren",
  },
  // TES-1150 — until the doctor has registered the prescription, the next
  // move is his, paid or not.
  approved: { waitingOn: "doctor", nextStep: CONFIRM_PRESCRIPTION_CTA },
  awaiting_subscription: {
    waitingOn: "doctor",
    nextStep: CONFIRM_PRESCRIPTION_CTA,
  },
  subscription_active_awaiting_prescription: {
    waitingOn: "doctor",
    nextStep: CONFIRM_PRESCRIPTION_CTA,
  },
  prescription_issued_awaiting_subscription: {
    waitingOn: "patient",
    nextStep: "Patienten aktiverar abonnemanget",
  },
  prescription_issued: {
    waitingOn: "patient",
    nextStep: "Patienten startar behandlingen",
  },
  treatment_started: null,
  transfer_declined: null,
  new_declined: null,
  paused: null,
  ended: null,
};

// The statuses that are still onboarding — Emelie: "Onboarding" may stay as
// the grouping, it just must not replace the precise status.
const AFTER_ONBOARDING = new Set<OnboardingStatusKey>([
  "treatment_started",
  "transfer_declined",
  "new_declined",
  "paused",
  "ended",
]);

// ---- Activities (the timeline's steps, S2; the card's "latest activity") ---

export const ONBOARDING_ACTIVITIES = [
  "f01_answered",
  "account_created",
  "email_verified",
  "app_login",
  "declaration_started",
  "declaration_saved",
  "declaration_submitted",
  "f07_started",
  "f07_saved",
  "f07_submitted",
  "meeting_booked",
  "meeting_held",
  "review_opened",
  "completion_requested",
  "decision_published",
  "subscription_link_sent",
  "subscription_activated",
  "prescription_issued",
  "treatment_started",
] as const;
export const onboardingActivitySchema = z.enum(ONBOARDING_ACTIVITIES);
export type OnboardingActivity = z.infer<typeof onboardingActivitySchema>;

// TES-1117: proposed wording, built from Emelie's timeline step names.
// "F-02B" is the transfer patient's declaration; a new-treatment patient's is
// "Hälsodeklaration" (Emelie's word in decision 2).
export function onboardingActivityLabel(
  activity: OnboardingActivity,
  journey: PatientGroup,
): string {
  const decl =
    journey === "PRESCRIPTION_TRANSFER" ? "F-02B" : "Hälsodeklaration";
  switch (activity) {
    case "f01_answered":
      return "F-01 besvarad";
    case "account_created":
      return "Konto skapat med BankID";
    case "email_verified":
      return "E-post bekräftad";
    case "app_login":
      return "Inloggad i appen";
    case "declaration_started":
      return `${decl} påbörjad`;
    case "declaration_saved":
      return `${decl} sparad som utkast`;
    case "declaration_submitted":
      return `${decl} inskickad`;
    case "f07_started":
      return "F-07 påbörjad";
    case "f07_saved":
      return "F-07 sparad som utkast";
    case "f07_submitted":
      return "F-07 inskickad";
    case "meeting_booked":
      return "Vårdmöte bokat";
    case "meeting_held":
      return "Vårdmöte genomfört";
    case "review_opened":
      return "Underlag inskickat";
    case "completion_requested":
      return "Komplettering begärd";
    case "decision_published":
      return "Behandlingsbeslut";
    case "subscription_link_sent":
      return "Abonnemangslänk skickad";
    case "subscription_activated":
      return "Abonnemang aktiverat";
    case "prescription_issued":
      return "Recept utfärdat";
    case "treatment_started":
      return "Behandling startad";
  }
}

// ---- The DTO -----------------------------------------------------------------

export const onboardingStatusSchema = z.object({
  journey: patientGroupSchema,
  status: onboardingStatusKeySchema,
  /** The precise status, in Swedish. */
  label: z.string(),
  /** "Inväntar medicinsk bedömning" once the journeys are equivalent. */
  sharedStatus: z.string().nullable(),
  /** The journey's detail under `sharedStatus`. */
  detail: z.string().nullable(),
  /** False once she has started, been declined, paused or ended. */
  inOnboarding: z.boolean(),
  waitingOn: onboardingWaitingOnSchema.nullable(),
  nextStep: z.string().nullable(),
  latestActivity: z
    .object({
      activity: onboardingActivitySchema,
      label: z.string(),
      at: z.string(),
    })
    .nullable(),
});
export type OnboardingStatus = z.infer<typeof onboardingStatusSchema>;

// ---- The facts and the derivation ---------------------------------------------

/** A form's progress: the server draft (FormDraft) and its submissions. */
export interface FormProgressFacts {
  /** FormDraft.startedAt — set by the first autosave, never moved. */
  draftStartedAt: Date | null;
  /** FormDraft.lastSavedAt. */
  draftSavedAt: Date | null;
  /** The first FormSubmission. */
  firstSubmittedAt: Date | null;
  /** The newest FormSubmission (a komplettering resubmits). */
  lastSubmittedAt: Date | null;
}

export const NO_FORM_PROGRESS: FormProgressFacts = {
  draftStartedAt: null,
  draftSavedAt: null,
  firstSubmittedAt: null,
  lastSubmittedAt: null,
};

export interface OnboardingFacts {
  journey: PatientGroup;
  /** Patient.journeyState. */
  journeyState: string;
  /** Treatment.state, null without a Treatment row. */
  treatmentState: string | null;
  /** Patient.createdAt: the first BankID sign-in made the row. */
  accountCreatedAt: Date;
  /** Her newest F-01 answer set, web lead or in-app. */
  f01At: Date | null;
  emailVerifiedAt: Date | null;
  /**
   * The first trace of her signed in in the app: a linked install (TES-1105),
   * an app-started BankID (TES-1088) or a push token. A form draft or
   * submission proves it too, and the derivation counts those itself.
   */
  appLoginAt: Date | null;
  /**
   * TES-1117 S2 — the web's app hand-off: her first
   * `onboarding_app_handoff_shown` or store-button click. Only a web start
   * has one.
   */
  appHandoffAt: Date | null;
  /** F-02 / F-02B (`intake`). */
  declaration: FormProgressFacts;
  /** F-07 (`prescription_transfer`). */
  transferForm: FormProgressFacts;
  /** The active (scheduled / in progress) first consultation, if any. */
  meetingBooked: { bookedAt: Date; scheduledAt: Date } | null;
  /** The first consultation that took place. */
  meetingHeldAt: Date | null;
  /**
   * TES-1117 S2 — when her first first-consult meeting was booked, whether
   * it is still ahead or already held (`meetingBooked` is the active one).
   */
  firstMeetingBookedAt: Date | null;
  /** The newest prescription-transfer review. */
  transferReview: {
    status: string;
    createdAt: Date;
    decidedAt: Date | null;
    completionRequestedAt: Date | null;
  } | null;
  /** The newest open or ready completion round on her case. */
  completionRound: {
    status: "open" | "ready";
    requestedAt: Date;
    readyAt: Date | null;
  } | null;
  /** The newest PUBLISHED treatment decision (publishedTreatmentDecision). */
  decision: "none" | "approved" | "declined";
  decisionAt: Date | null;
  subscriptionLinkSentAt: Date | null;
  /** The entitlement rule (trialing or active). */
  subscriptionActive: boolean;
  subscriptionActivatedAt: Date | null;
  /**
   * The first cycle's `activatedAt`: since TES-1150 the RELEASE of the
   * registered prescription by a verified subscription.
   */
  prescriptionConfirmedAt: Date | null;
  /**
   * TES-1150 — the doctor's registration of the prescription
   * (`prescriptionRegisteredAt`), before the subscription. Optional so an
   * older caller reads as before.
   */
  prescriptionRegisteredAt?: Date | null;
  /** `first_dose_reported`. */
  firstDoseAt: Date | null;
  /**
   * TES-1602 — she is on the optional care meeting track (TES-1601: the
   * tenant flag, NEW_TREATMENT, a positive F-01) and no meeting has been
   * required. Optional so an older caller reads as before.
   */
  careMeetingOptional?: boolean;
  /**
   * TES-1602 — the doctor's "Kräv vårdmöte" (Patient.careMeetingRequiredAt).
   * TES-1643 — or flags in her health declaration; same status either way.
   */
  careMeetingRequired?: boolean;
  /**
   * TES-1646 (Ö-1015) — her stored choice of assessment after the
   * declaration (Patient.assessmentChoice), read on the optional track only.
   * Optional so an older caller reads as before: undefined counts as the
   * written assessment, the only path that track had.
   */
  assessmentChoice?: "written" | "meeting" | null;
  /**
   * TES-1650 (Ö-1020 b) — her written-track material is complete and handed
   * over to the doctor (Patient.writtenAssessmentHandedOverAt set). Optional
   * so an older caller reads as before: undefined counts as handed over.
   */
  writtenHandedOver?: boolean;
}

const TREATMENT_ERA = new Set([
  "treatment_started",
  "active_treatment",
  "maintenance",
]);

function hasDraft(f: FormProgressFacts): boolean {
  return f.draftStartedAt !== null;
}
function submitted(f: FormProgressFacts): boolean {
  return f.firstSubmittedAt !== null;
}

/** The precise status. Most advanced fact first. */
export function onboardingStatusKey(f: OnboardingFacts): OnboardingStatusKey {
  const transfer = f.journey === "PRESCRIPTION_TRANSFER";

  if (f.journeyState === "ended" || f.treatmentState === "ended")
    return "ended";
  if (f.treatmentState === "paused" || f.journeyState === "paused")
    return "paused";

  // The TES-1069 chain. The prescription is the final transaction; before it
  // the shared state machine says where a positive decision stands.
  if (f.prescriptionConfirmedAt) {
    return f.firstDoseAt ? "treatment_started" : "prescription_issued";
  }
  // Patients activated before TES-1069 have no confirmation to read.
  if (f.treatmentState === "active" || TREATMENT_ERA.has(f.journeyState)) {
    return "treatment_started";
  }
  if (f.decision === "approved") {
    switch (
      treatmentStartState({
        decision: "approved",
        subscriptionLinkSent: f.subscriptionLinkSentAt !== null,
        subscriptionActive: f.subscriptionActive,
        prescriptionRegistered: Boolean(f.prescriptionRegisteredAt),
        released: false,
        medicationPickupRequired: null,
        medicationPickedUp: false,
        firstDoseReported: false,
      })
    ) {
      case "PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION":
        return "prescription_issued_awaiting_subscription";
      case "SUBSCRIPTION_ACTIVATED_AWAITING_PRESCRIPTION":
        return "subscription_active_awaiting_prescription";
      case "AWAITING_SUBSCRIPTION":
        return "awaiting_subscription";
      default:
        return "approved";
    }
  }
  if (f.decision === "declined") {
    return transfer ? "transfer_declined" : "new_declined";
  }

  // An open komplettering is hers until every item is in, then the doctor's.
  if (f.completionRound) {
    return f.completionRound.status === "ready"
      ? "completion_ready"
      : "awaiting_completion";
  }

  if (transfer) {
    switch (f.transferReview?.status) {
      case "completion_requested":
        return "awaiting_completion";
      case "labs_required":
        return "awaiting_blood_test";
      case "meeting_required":
        return f.meetingBooked
          ? "transfer_meeting_booked"
          : "transfer_meeting_required";
      case "pending_review":
        return "transfer_submitted";
    }
    // Emelie, decision 3: the ladder before the doctor. Saved draft and not
    // started are told apart by the server draft (FormDraft), which the app
    // writes on the first answer.
    if (submitted(f.declaration) && submitted(f.transferForm))
      return "transfer_submitted";
    if (submitted(f.declaration)) {
      return hasDraft(f.transferForm)
        ? "transfer_f07_in_progress"
        : "transfer_f02b_done";
    }
    if (hasDraft(f.declaration)) return "transfer_declaration_in_progress";
    if (f.appLoginAt || hasDraft(f.transferForm))
      return "transfer_declaration_not_started";
    return f.f01At ? "transfer_f01_done" : "f01_not_started";
  }

  if (f.journeyState === "labs_required") return "awaiting_blood_test";
  if (f.journeyState === "labs_completed") return "blood_test_results_in";
  if (
    f.meetingHeldAt ||
    f.journeyState === "doctor_visit_completed" ||
    f.journeyState === "treatment_decision_ready"
  ) {
    return "new_meeting_held";
  }
  if (f.meetingBooked) {
    return submitted(f.declaration)
      ? "new_meeting_booked_declaration_done"
      : "new_meeting_booked_declaration_remaining";
  }
  // TES-1602 — the optional track. A required meeting not yet booked is hers
  // to book; a declaration in with no meeting is the doctor's to assess, as
  // a transfer's is.
  if (f.careMeetingRequired) return "new_meeting_required";
  if (f.careMeetingOptional && submitted(f.declaration)) {
    // TES-1646 — the declaration goes to the doctor's written assessment
    // only once she has chosen it.
    if (f.assessmentChoice === null) return "new_assessment_choice_pending";
    if (f.assessmentChoice === "meeting") return "new_meeting_chosen";
    // TES-1650 — and only once her material is complete and handed over.
    if (f.writtenHandedOver === false) return "new_written_material_remaining";
    return "new_declaration_submitted";
  }
  // TES-1650 (Ö-1006) — started, not sent.
  if (f.careMeetingOptional && hasDraft(f.declaration)) {
    return "new_declaration_in_progress";
  }
  return f.f01At ? "new_f01_done" : "f01_not_started";
}

/** Every dated fact, for "latest relevant activity" (and S2's timeline). */
export function onboardingActivities(
  f: OnboardingFacts,
): Array<{ activity: OnboardingActivity; at: Date }> {
  const out: Array<{ activity: OnboardingActivity; at: Date | null }> = [
    { activity: "account_created", at: f.accountCreatedAt },
    { activity: "f01_answered", at: f.f01At },
    { activity: "email_verified", at: f.emailVerifiedAt },
    { activity: "app_login", at: f.appLoginAt },
    { activity: "declaration_started", at: f.declaration.draftStartedAt },
    // A save after the last submission is a reopened draft (a komplettering).
    {
      activity: "declaration_saved",
      at: savedAfterSubmission(f.declaration),
    },
    { activity: "declaration_submitted", at: f.declaration.lastSubmittedAt },
    { activity: "f07_started", at: f.transferForm.draftStartedAt },
    { activity: "f07_saved", at: savedAfterSubmission(f.transferForm) },
    { activity: "f07_submitted", at: f.transferForm.lastSubmittedAt },
    { activity: "meeting_booked", at: f.meetingBooked?.bookedAt ?? null },
    { activity: "meeting_held", at: f.meetingHeldAt },
    { activity: "review_opened", at: f.transferReview?.createdAt ?? null },
    {
      activity: "completion_requested",
      at:
        f.completionRound?.requestedAt ??
        f.transferReview?.completionRequestedAt ??
        null,
    },
    { activity: "decision_published", at: f.decisionAt },
    { activity: "subscription_link_sent", at: f.subscriptionLinkSentAt },
    { activity: "subscription_activated", at: f.subscriptionActivatedAt },
    // TES-1150 — the prescription is issued at the doctor's registration;
    // the release is when she could see it.
    {
      activity: "prescription_issued",
      at: f.prescriptionRegisteredAt ?? f.prescriptionConfirmedAt,
    },
    { activity: "treatment_started", at: f.firstDoseAt },
  ];
  return out
    .filter((a): a is { activity: OnboardingActivity; at: Date } => !!a.at)
    .sort((a, b) => a.at.getTime() - b.at.getTime());
}

function savedAfterSubmission(f: FormProgressFacts): Date | null {
  // The first autosave is the start itself; only a later save is news.
  if (!f.draftSavedAt || !f.draftStartedAt) return null;
  if (f.draftSavedAt <= f.draftStartedAt) return null;
  if (f.lastSubmittedAt && f.draftSavedAt <= f.lastSubmittedAt) return null;
  return f.draftSavedAt;
}

export function deriveOnboardingStatus(f: OnboardingFacts): OnboardingStatus {
  const status = onboardingStatusKey(f);
  const shared = AWAITING_MEDICAL_ASSESSMENT.has(status);
  const next = NEXT[status];
  const activities = onboardingActivities(f);
  const latest = activities[activities.length - 1];
  return {
    journey: f.journey,
    status,
    label: ONBOARDING_STATUS_LABEL[status],
    sharedStatus: shared ? AWAITING_MEDICAL_ASSESSMENT_LABEL : null,
    detail: shared
      ? status === "transfer_submitted" ||
        status === "new_declaration_submitted"
        ? TRANSFER_ASSESSMENT_DETAIL
        : ONBOARDING_STATUS_LABEL[status]
      : null,
    inOnboarding: !AFTER_ONBOARDING.has(status),
    waitingOn: next?.waitingOn ?? null,
    nextStep: next?.nextStep ?? null,
    latestActivity: latest
      ? {
          activity: latest.activity,
          label: onboardingActivityLabel(latest.activity, f.journey),
          at: latest.at.toISOString(),
        }
      : null,
  };
}
